/**
 * @file Armes possedees : gacha (normal/epic + pitie), fusion (manuelle/auto), equipement par slot, verrou, coffre (6+10), collection.
 */
import { Decimal } from '@shared/bignum';
import { GRADES, gradeIndex } from '@shared/enums';
import type { GradeId, WalletId } from '@shared/enums';
import { fail, ok, okVoid } from '@shared/types';
import type { Result, Uid } from '@shared/types';
import type { GachaPoolId } from '@config/types';
import { dayKey } from '../Clock';
import type { SimContext } from '../Context';
import { resolveAmounts } from '../economy/Wallet';
import type { ResolvedAmounts } from '../economy/Wallet';
import type { WeaponItem } from '../state/PlayerData';

export class WeaponService {
  /** Fusion automatique en cours (une paire toutes les merge.autoStepSeconds) ; etat d'execution, non persiste. */
  private autoQueued = false;
  private autoAcc = 0;

  constructor(private readonly ctx: SimContext) {}

  // ------------------------------------------------------------------ lectures

  get items(): readonly WeaponItem[] {
    return this.ctx.data.weapons.items;
  }

  item(uid: Uid): WeaponItem | undefined {
    return this.ctx.data.weapons.items.find((w) => w.uid === uid);
  }

  isEquipped(uid: Uid): boolean {
    return this.ctx.data.weapons.equipped.includes(uid);
  }

  /** Nombre de slots d'equipement debloques (BALANCE.equipSlotUnlocks vs record de stage). */
  unlockedSlots(): number {
    const best = this.ctx.data.progress.bestStageValue;
    return this.ctx.config.balance.equipSlotUnlocks.filter((v) => best >= v).length;
  }

  /** Capacite du coffre (armes NON equipees). */
  chestCapacity(): number {
    return this.ctx.config.balance.chest.freeSlots + this.ctx.data.weapons.chestPaidSlots;
  }

  chestCount(): number {
    return this.items.filter((w) => !this.isEquipped(w.uid)).length;
  }

  chestFull(): boolean {
    return this.chestCount() >= this.chestCapacity();
  }

  /** Genere un uid stable et unique : "w<seq>" (seq persiste dans weapons.nextSeq). */
  newUid(): Uid {
    return `w${this.ctx.data.weapons.nextSeq++}`;
  }

  private gradeOf(defId: string): GradeId {
    return this.ctx.index.weapon(defId).grade;
  }

  // ------------------------------------------------------------------ gacha

  /** Tirage payant (1 ou multiPullCount) ; refuse si le coffre deborde. Declenche la fusion auto si activee. */
  draw(pool: GachaPoolId, count: number): Result<WeaponItem[]> {
    const ctx = this.ctx;
    const spec = ctx.config.balance.gacha[pool];
    if (count !== 1 && count !== spec.multiPullCount) return fail('invalidArgument');
    if (this.chestCount() + count > this.chestCapacity()) return fail('inventoryFull');
    const unit = resolveAmounts(spec.cost);
    const cost: ResolvedAmounts = {};
    for (const [id, v] of Object.entries(unit) as Array<[WalletId, Decimal]>) cost[id] = v.times(count);
    const r = ctx.tx.purchase(cost, `gacha:${pool}`, () => ok(this.rollMany(pool, count)));
    if (!r.ok) return r;
    this.afterDraw(pool, r.value, count);
    return r;
  }

  /** Tirage gratuit (recompense) : sans cout ni verification de capacite (debordement autorise). */
  drawFree(pool: GachaPoolId, count: number): WeaponItem[] {
    const items = this.rollMany(pool, count);
    this.afterDraw(pool, items, count);
    return items;
  }

  private afterDraw(pool: GachaPoolId, items: WeaponItem[], count: number): void {
    const ctx = this.ctx;
    ctx.services.missions.increment('gunDraws', count);
    let best: GradeId = 'D';
    for (const it of items) {
      const g = this.gradeOf(it.defId);
      if (gradeIndex(g) > gradeIndex(best)) best = g;
    }
    ctx.bus.emit('WeaponDrawn', { pool, uids: items.map((i) => i.uid), defIds: items.map((i) => i.defId), bestGrade: best });
    if (ctx.data.weapons.autoMerge) this.queueAutoMerge();
  }

  private rollMany(pool: GachaPoolId, count: number): WeaponItem[] {
    const out: WeaponItem[] = [];
    for (let i = 0; i < count; i++) out.push(this.rollOne(pool));
    return out;
  }

  /** Tirage d'UNE arme : grade pondere, pitie (grade minimal garanti tous les N tirages), arme au hasard dans le grade. */
  rollOne(pool: GachaPoolId): WeaponItem {
    const ctx = this.ctx;
    const spec = ctx.config.balance.gacha[pool];
    let grade = (ctx.rng.gacha.weighted(spec.table, (e) => e.weight) ?? spec.table[0])?.grade ?? 'D';
    if (spec.pity) {
      const w = ctx.data.weapons;
      w.pity[pool]++;
      if (gradeIndex(grade) >= gradeIndex(spec.pity.minGrade)) {
        w.pity[pool] = 0;
      } else if (w.pity[pool] >= spec.pity.every) {
        grade = spec.pity.minGrade;
        w.pity[pool] = 0;
      }
    }
    const def = ctx.rng.gacha.pick(ctx.index.weaponsOfGrade(grade));
    return this.grantWeapon(def.id, 'gacha');
  }

  /** Ajoute l'arme a l'inventaire et met a jour la collection (WeaponCollected). */
  grantWeapon(defId: string, _reason: string): WeaponItem {
    const w = this.ctx.data.weapons;
    this.ctx.index.weapon(defId); // valide l'id
    const uid = this.newUid();
    const item: WeaponItem = { uid, defId, locked: false, obtainedSeq: w.nextSeq - 1 };
    w.items.push(item);
    const entry = w.collection[defId] ?? { seen: false, obtained: 0 };
    const firstTime = !entry.seen;
    entry.seen = true;
    entry.obtained++;
    w.collection[defId] = entry;
    this.ctx.bus.emit('WeaponCollected', { defId, firstTime });
    return item;
  }

  // ------------------------------------------------------------------ fusion

  /**
   * Regle de fusion (BALANCE.merge.rule) : 'identical' = meme defId, 'sameGrade' (defaut) = meme grade, types differents acceptes ; uids differents, aucune verrouillee,
   * pas de grade maximal ; au plus UNE des deux equipee (le resultat reprend son emplacement). Deux armes equipees ne fusionnent pas
   * (on perdrait un chat sans le vouloir) ; la fusion auto, elle, ne touche jamais aux armes equipees (voir nextAutoPair).
   */
  canMerge(a: Uid, b: Uid): boolean {
    if (a === b) return false;
    const ia = this.item(a);
    const ib = this.item(b);
    if (!ia || !ib || ia.locked || ib.locked) return false;
    if (this.isEquipped(a) && this.isEquipped(b)) return false;
    const rule = this.ctx.config.balance.merge.rule;
    const ga = this.gradeOf(ia.defId);
    if (gradeIndex(ga) >= GRADES.length - 1) return false;
    return rule === 'identical' ? ia.defId === ib.defId : ga === this.gradeOf(ib.defId);
  }

  /** Fusionne deux armes en UNE arme aleatoire du grade suivant ; le resultat reprend le slot equipe eventuel. */
  merge(a: Uid, b: Uid, auto = false): Result<WeaponItem> {
    const ctx = this.ctx;
    if (!this.canMerge(a, b)) return fail('cannotMerge');
    const w = ctx.data.weapons;
    const ia = this.item(a) as WeaponItem;
    const next = GRADES[gradeIndex(this.gradeOf(ia.defId)) + 1] as GradeId;
    const def = ctx.rng.gacha.pick(ctx.index.weaponsOfGrade(next));
    const slotA = w.equipped.indexOf(a);
    const slotB = w.equipped.indexOf(b);
    const slot = slotA >= 0 ? slotA : slotB;
    w.items = w.items.filter((i) => i.uid !== a && i.uid !== b);
    if (slot >= 0) w.equipped[slot] = null;
    const result = this.grantWeapon(def.id, 'merge');
    ctx.bus.emit('WeaponsMerged', { consumed: [a, b], result: result.uid, resultDefId: def.id, auto });
    ctx.services.missions.increment('gunMerges', 1);
    if (slot >= 0) {
      w.equipped[slot] = result.uid;
      ctx.bus.emit('WeaponEquipped', { slot, uid: result.uid });
      ctx.bus.emit('ModifiersDirty', { source: 'weapons' });
      ctx.services.team.rebuild();
    }
    return ok(result);
  }

  /** Paire a fusionner en priorite (grade le plus bas, armes les plus anciennes) ; les grades >= autoMergeExcludeFromGradeIndex sont ignores. */
  private nextAutoPair(): [Uid, Uid] | null {
    const limit = this.ctx.config.balance.merge.autoMergeExcludeFromGradeIndex;
    const sorted = this.items
      .filter((i) => gradeIndex(this.gradeOf(i.defId)) < limit && !this.isEquipped(i.uid))
      .sort((x, y) => gradeIndex(this.gradeOf(x.defId)) - gradeIndex(this.gradeOf(y.defId)) || x.obtainedSeq - y.obtainedSeq);
    for (let i = 0; i < sorted.length; i++) {
      for (let j = i + 1; j < sorted.length; j++) {
        const x = sorted[i] as WeaponItem;
        const y = sorted[j] as WeaponItem;
        if (this.canMerge(x.uid, y.uid)) return [x.uid, y.uid];
      }
    }
    return null;
  }

  /** Une seule fusion automatique (la paire prioritaire) ; false s'il n'y a plus rien a fusionner. */
  autoMergeStep(): boolean {
    const pair = this.nextAutoPair();
    return pair !== null && this.merge(pair[0], pair[1], true).ok;
  }

  /** Fusion automatique immediate jusqu'au point fixe (outils / tests) ; le jeu passe par queueAutoMerge (une paire a la fois). */
  autoMergeAll(): number {
    let merges = 0;
    while (this.autoMergeStep()) merges++;
    this.autoQueued = false;
    return merges;
  }

  /**
   * Programme la fusion automatique : une paire tout de suite, puis une toutes les `merge.autoStepSeconds` (tickAutoMerge) jusqu'au point fixe.
   * Chaque palier (D+D -> C, puis C+C -> B) est ainsi visible au lieu d'une cascade instantanee qui semblait sauter un grade.
   * Renvoie true si au moins une fusion a eu lieu.
   */
  queueAutoMerge(): boolean {
    const merged = this.autoMergeStep();
    this.autoQueued = merged;
    this.autoAcc = 0;
    return merged;
  }

  /** A appeler a chaque tic (TimersSystem, tourne aussi en pause : l'ecran Armes peut etre ouvert). */
  tickAutoMerge(dt: number): void {
    if (!this.autoQueued) return;
    this.autoAcc += dt;
    if (this.autoAcc < this.ctx.config.balance.merge.autoStepSeconds) return;
    this.autoAcc = 0;
    if (!this.autoMergeStep()) this.autoQueued = false;
  }

  /** Fusions automatiques encore en attente (UI : bouton « Tout fusionner » en cours). */
  get autoMerging(): boolean {
    return this.autoQueued;
  }

  // ------------------------------------------------------------------ equipement

  /** Equipe `uid` dans `slot` (echange si l'arme est deja dans un autre slot). */
  equip(uid: Uid, slot: number): Result {
    const ctx = this.ctx;
    const w = ctx.data.weapons;
    if (slot < 0 || slot >= w.equipped.length) return fail('invalidArgument');
    if (slot >= this.unlockedSlots()) return fail('locked');
    if (!this.item(uid)) return fail('notOwned');
    const prev = w.equipped[slot] ?? null;
    const from = w.equipped.indexOf(uid);
    if (from === slot) return okVoid;
    if (from >= 0) {
      w.equipped[from] = prev;
      ctx.bus.emit('WeaponEquipped', { slot: from, uid: prev });
    }
    w.equipped[slot] = uid;
    ctx.bus.emit('WeaponEquipped', { slot, uid });
    ctx.bus.emit('ModifiersDirty', { source: 'weapons' });
    ctx.services.team.rebuild();
    return okVoid;
  }

  /** Vide un slot (le slot 0 ne peut pas etre vide s'il reste la seule arme equipee). */
  unequip(slot: number): Result {
    const ctx = this.ctx;
    const w = ctx.data.weapons;
    const uid = w.equipped[slot];
    if (slot < 0 || slot >= w.equipped.length) return fail('invalidArgument');
    if (!uid) return okVoid;
    if (slot === 0 && w.equipped.filter(Boolean).length <= 1) return fail('invalidArgument');
    if (this.chestFull()) return fail('inventoryFull');
    w.equipped[slot] = null;
    ctx.bus.emit('WeaponEquipped', { slot, uid: null });
    ctx.bus.emit('ModifiersDirty', { source: 'weapons' });
    ctx.services.team.rebuild();
    return okVoid;
  }

  /** Verrouille/deverrouille (arme "figee" : exclue des fusions). */
  setLock(uid: Uid, locked: boolean): Result {
    const it = this.item(uid);
    if (!it) return fail('notOwned');
    it.locked = locked;
    return okVoid;
  }

  // ------------------------------------------------------------------ coffre, collection, quotidien

  buyChestSlot(): Result {
    const ctx = this.ctx;
    const chest = ctx.config.balance.chest;
    const bought = ctx.data.weapons.chestPaidSlots;
    if (bought >= chest.paidSlots) return fail('maxLevel');
    const cost = chest.paidSlotCost[bought];
    if (!cost) return fail('maxLevel');
    return ctx.tx.purchase(cost, 'chestSlot', () => {
      ctx.data.weapons.chestPaidSlots++;
      return okVoid;
    });
  }

  /** Recompense de collection "grade:<G>" : toutes les armes du grade vues => { dia: 50 x (index+1) }, une seule fois. */
  claimCollection(grade: GradeId): Result {
    const ctx = this.ctx;
    const w = ctx.data.weapons;
    const key = `grade:${grade}`;
    if (w.collectionClaimed.includes(key)) return fail('nothingToClaim');
    const defs = ctx.index.weaponsOfGrade(grade);
    if (defs.length === 0 || !defs.every((d) => w.collection[d.id]?.seen)) return fail('nothingToClaim');
    w.collectionClaimed.push(key);
    ctx.tx.grantBundle({ wallet: { dia: new Decimal(50 * (gradeIndex(grade) + 1)) } }, `collection:${grade}`);
    return okVoid;
  }

  /** Tirage gratuit quotidien (1 normal par jour UTC). */
  dailyFreeDraw(): Result<WeaponItem[]> {
    const w = this.ctx.data.weapons;
    const day = dayKey(this.ctx.rt.wallMs);
    if (w.dailyFreeDrawDay === day) return fail('cooldown');
    w.dailyFreeDrawDay = day;
    return ok(this.drawFree('normal', 1));
  }
}
